/** Family 3: cube storage — a layout mode of the shared case generator (plan §4.7). */
import { frontsParameterDef } from '../fronts';
import jointsJson from './joints.json' with { type: 'json' };
import type { Family } from '../registry-types';
import type { FamilyDefinition } from '../types';
import { mm } from '../../domain/units';
import { baseParameterDefs, setBaseParam, sideParameterDefs } from '../base';
import { generateCube } from './generate';
import { checkCubeParams, CUBE_INTAKE, cubeDesignFromFit, cubeFitSummary, type CubeFitParams } from './intake';
import { createCube, CUBE_ID, CUBE_LABEL, CUBE_PRESETS, CUBE_VERSION, cubeParamsOf, setCubeParam, type CubeParams, type CubePresetKey } from './design';

export const CUBE_DEFINITION: FamilyDefinition = Object.freeze({
  id: CUBE_ID,
  version: CUBE_VERSION,
  label: CUBE_LABEL,
  description: 'A closed box divided into a lattice of equal square cells.',
  parameters: [
    { key: 'W', type: 'length', fieldKey: 'W', range: { min: mm(300), max: mm(2000), step: 10 }, default: mm(1200), unit: 'mm', critical: true, ui: { label: 'Width', control: 'field', group: 'Overall size' } },
    { key: 'H', type: 'length', fieldKey: 'H', range: { min: mm(300), max: mm(2000), step: 10 }, default: mm(1200), unit: 'mm', critical: true, ui: { label: 'Height', control: 'field', group: 'Overall size' } },
    { key: 'D', type: 'length', fieldKey: 'D', range: { min: mm(200), max: mm(450), step: 10 }, default: mm(350), unit: 'mm', critical: true, ui: { label: 'Depth', control: 'field', group: 'Overall size' } },
    { key: 'cols', type: 'count', range: { min: 1, max: 6, step: 1 }, default: 3, unit: 'count', critical: false, ui: { label: 'Columns', help: 'Cells side by side; they stay equal.', control: 'stepper', group: 'Cells' } },
    { key: 'rows', type: 'count', range: { min: 1, max: 6, step: 1 }, default: 3, unit: 'count', critical: false, ui: { label: 'Rows', help: 'Cells one above another; they stay equal.', control: 'stepper', group: 'Cells' } },
    {
      key: 'intersection', type: 'enum', default: 'egg-crate', critical: false,
      options: [
        { value: 'egg-crate', label: 'Egg-crate', help: 'Full-length dividers and shelves cross in halving joints.' },
        { value: 'housed', label: 'Housed', help: 'Full-width shelves with short dividers housed above and below.' },
      ],
      ui: { label: 'Intersections', control: 'choice', group: 'Construction' },
    },
    {
      key: 'back', type: 'enum', default: 'none', critical: false,
      options: [
        { value: 'none', label: 'None', help: 'Open back: fix it to the wall to stop racking.' },
        { value: 'overlay', label: 'Overlay', help: 'Screwed over the back edges.' },
        { value: 'rebated', label: 'Rebated', help: 'Set into a rebate in the sides, top and bottom.' },
      ],
      ui: { label: 'Back', control: 'choice', group: 'Construction' },
    },
    ...sideParameterDefs(),
    ...baseParameterDefs(),
    frontsParameterDef(),
    {
      key: 'stock', type: 'enum', fieldKey: 'stockThickness', default: 'birch-ply-18', critical: true,
      options: [
        { value: 'birch-ply-18', label: 'Birch plywood 18 mm' },
        { value: 'sheesham-board-25', label: 'Sheesham 25 mm' },
        { value: 'oak-board-20', label: 'Oak 20 mm' },
        { value: 'sheesham-board-38', label: 'Sheesham 38 mm' },
        { value: 'sheesham-board-45', label: 'Sheesham 45 mm' },
        { value: 'oak-board-38', label: 'Oak 38 mm' },
        { value: 'oak-board-45', label: 'Oak 45 mm' },
        { value: 'sheesham-board-70', label: 'Sheesham 70 mm slab' },
        { value: 'oak-board-70', label: 'Oak 70 mm slab' },
      ],
      ui: { label: 'Material', help: 'Chosen in Look.', control: 'choice', group: 'Construction', step: 'look' },
    },
  ],
  presets: (Object.entries(CUBE_PRESETS) as [string, (typeof CUBE_PRESETS)[CubePresetKey]][]).map(([id, p]) => ({ id, label: p.label, description: p.description, params: { W: p.W, H: p.H, D: p.D, ...p.params } })),
  handles: [],
}) as FamilyDefinition;

export const CUBE_FAMILY: Family<CubeFitParams> = {
  definition: CUBE_DEFINITION,
  jointsJson,
  intake: CUBE_INTAKE,
  generate: (design, stock, construction) => generateCube(design, stock, construction),
  checkParams: checkCubeParams,
  designFromFit: cubeDesignFromFit,
  fitSummary: cubeFitSummary,
  create: (presetId?: string) => createCube((presetId as CubePresetKey) ?? 'three-by-three'),
  setParam: (design, key, value) => {
    const b = setBaseParam(cubeParamsOf(design), key, value);
    if (b) return { ...design, appearance: { ...design.appearance }, params: b as unknown as Record<string, unknown> };
    return setCubeParam(design, key as keyof CubeParams, value as never);
  },
};

export * from './design';
export * from './generate';
export * from './intake';
